import * as uuid from "uuid"
import { Observable, of, defer, EMPTY } from "rxjs"
import { switchMap, catchError, filter, mergeMap } from "rxjs/operators"
import { omit } from "ramda"
import { ofType, StateObservable } from "redux-observable"
import { Router } from "../../../../server/router"
import type { EpicDependencies } from "../../../rxjs/configureEpics"
import {
    CLOSE_PRICING_FORM_CLICKED,
    DELETE_PRICING_CLICKED,
    fetchSpecialPricingsFailure,
    fetchSpecialPricingsSuccess,
    PRICING_FORM_SUBMITTED,
    PRICING_LOCATION_SAVE_REQUESTED,
    PRICING_MOVED,
    PRICING_SAVE_REQUESTED,
    PRICING_SORT_REQUESTED,
    pricingLocationSaveFailure,
    pricingLocationSaveRequested,
    pricingLocationSaveSuccess,
    pricingSaveFailure,
    pricingSaveRequested,
    pricingSaveSuccess,
    pricingSortRequested,
    REFETCH_PRICINGS_DATA,
} from "./actions"
import { getSelectedLocationOverrideId } from "../../../common/locationsPicker/selectors"
import { createActionLoadingError } from "../../../data/actions"
import { isPricingsAgendaOpened, isPromotionsAgendaOpened } from "../../../data/selectors"
import { ACCOUNT_SELECTED, AGENDA_MOUNTED, REFETCH_DATA } from "../../../auth/actions"
import { createLoadingEnded, createLoadingStarted } from "../../../filters/actions"
import { getHttpHeaders } from "../../../auth/selectors"
import { fetchSpecialPricingsData, pricingDeleteApi, pricingLocationSave, pricingSave, pricingSort } from "./request"
import { getPricingsData } from "./selectors"
import type { PricingLocationSaveRequestData, PricingSaveRequestData } from "../../../../graphql/types"
import { PricingType } from "../../../../graphql/types"
import type { PricingFormItem } from "./PricingForm"
import { isMibSettingsAgendaOpened } from "../../settings/selectors"
import { logger } from "../../../../utils/loggerWrap"
import { Action, StateObject } from "../../../redux/types"
import { constructAuditData } from "../../auditLog/utils/utils-payload-data"

export const specialPricingsLoadingStartedEpics = (
    actions$: Observable<Action>,
    state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA, AGENDA_MOUNTED, REFETCH_PRICINGS_DATA),
        filter(
            () =>
                isPricingsAgendaOpened(deps.getState()) ||
                isMibSettingsAgendaOpened(deps.getState()) ||
                isPromotionsAgendaOpened(deps.getState())
        ),
        switchMap(() => [createLoadingStarted("pricing")])
    )

export const fetchSpecialPricingsDataRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(ACCOUNT_SELECTED, REFETCH_DATA, AGENDA_MOUNTED, REFETCH_PRICINGS_DATA),
        filter(
            () =>
                isPricingsAgendaOpened(deps.getState()) ||
                isMibSettingsAgendaOpened(deps.getState()) ||
                isPromotionsAgendaOpened(deps.getState())
        ),
        switchMap(() =>
            defer(() => fetchSpecialPricingsData(getHttpHeaders(deps.getState()))).pipe(
                switchMap((response): any => {
                    if (response) {
                        return [fetchSpecialPricingsSuccess(response), createLoadingEnded("pricing")]
                    }

                    return [fetchSpecialPricingsFailure(), createLoadingEnded("pricing")]
                }),
                catchError(error => of(fetchSpecialPricingsFailure(error)))
            )
        )
    )

export const pricingFormSubmittedEpicsNew = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PRICING_FORM_SUBMITTED),
        switchMap((action: Action) => {
            logger.info("pricingFormSubmittedEpicsNew - form submitted", {
                data: action.payload,
            })

            const pricings = getPricingsData(deps.getState())
            const lastSortIndex = pricings && pricings.data ? pricings.data.length : 0

            const auditData = constructAuditData(deps, true)

            if (!action.payload.oldValue) {
                // New
                const pricing: PricingFormItem = action.payload.newValue
                const data: PricingSaveRequestData = {
                    id: uuid.v4(),
                    name: pricing.name,
                    absolute: pricing.type === "absolute" ? pricing.value : null,
                    percentage: pricing.type === "percentage" ? pricing.value : null,
                    isOrderLevel: pricing.isOrderLevel,
                    isItemLevel: pricing.isItemLevel,
                    // @ts-expect-error
                    isActive: pricing.isApplicable,
                    isAutoApply: pricing.autoApply,
                    sortIndex: lastSortIndex + 1,
                    autoApplyHours: pricing.autoApplyHours ? pricing.autoApplyHours.timeSpans : null,
                    isCreating: true,
                    auditData: auditData,
                }
                return [pricingSaveRequested(data)]
            } else {
                const selectedLocationId = getSelectedLocationOverrideId(deps.getState())

                if (selectedLocationId) {
                    const pricing: PricingFormItem = action.payload.newValue
                    const data: PricingLocationSaveRequestData = {
                        // @ts-expect-error
                        id: pricing.pricingId,
                        locationId: selectedLocationId,
                        // @ts-expect-error
                        isActive: pricing.isApplicable,
                        isAutoApply: pricing.autoApply,
                        autoApplyHours: pricing.autoApplyHours ? pricing.autoApplyHours.timeSpans : null,
                        auditData: auditData,
                    }
                    return [pricingLocationSaveRequested(data, selectedLocationId)]
                } else {
                    const pricing: PricingFormItem = action.payload.newValue
                    const data: PricingSaveRequestData = {
                        // @ts-expect-error
                        id: pricing.pricingId,
                        name: pricing.name,
                        absolute: pricing.type === "absolute" ? pricing.value : null,
                        percentage: pricing.type === "percentage" ? pricing.value : null,
                        isOrderLevel: pricing.isOrderLevel,
                        isItemLevel: pricing.isItemLevel,

                        // @ts-expect-error
                        isActive: pricing.isApplicable,
                        isAutoApply: pricing.autoApply,

                        // @ts-expect-error
                        sortIndex: pricing.sortIndex,
                        autoApplyHours: pricing.autoApplyHours ? pricing.autoApplyHours.timeSpans : null,
                        isCreating: false,
                        auditData: auditData,
                    }
                    return [pricingSaveRequested(data)]
                }
            }
        })
    )

export const pricingSaveRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PRICING_SAVE_REQUESTED),
        mergeMap((action: Action) => {
            return defer(() => pricingSave(getHttpHeaders(deps.getState()), action.payload.data)).pipe(
                switchMap(response => {
                    const responseData = response && response.data

                    if (responseData) {
                        return [pricingSaveSuccess({ ...responseData, sortIndex: action.payload.data.sortIndex })]
                    }

                    const data: PricingSaveRequestData = action.payload.data
                    if (
                        data.pricingType === PricingType.DeliveryFee ||
                        data.pricingType === PricingType.PromoCodeDiscount ||
                        data.pricingType === PricingType.FirstTimePromoDiscount ||
                        data.pricingType === PricingType.MoFirstOrderPromoDiscount
                    ) {
                        return [
                            pricingSaveSuccess({
                                ...omit(["isCreating", "auditData", "pricingType", "autoApplyHours"], data),
                                autoApplyHours: null,
                            }),
                        ]
                    }

                    return [pricingSaveFailure("Pricing save returned no data")]
                }),
                catchError(error => of(pricingSaveFailure(error)))
            )
        })
    )

export const pricingLocationSaveRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PRICING_LOCATION_SAVE_REQUESTED),
        switchMap((action: Action) => {
            const auditData = constructAuditData(deps, false)
            return defer(() =>
                pricingLocationSave(getHttpHeaders(deps.getState()), { ...action.payload.data, auditData: auditData })
            ).pipe(
                switchMap(response => {
                    if (response) {
                        return [
                            pricingLocationSaveSuccess(response.data, action.payload.data, action.payload.locationId),
                        ]
                    }

                    return [pricingLocationSaveFailure("Pricing location save returned no data")]
                }),
                catchError(error => of(pricingLocationSaveFailure(error)))
            )
        })
    )

export const deletePricingClickedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(DELETE_PRICING_CLICKED),
        switchMap((action: Action) => {
            const auditData = constructAuditData(deps, false)
            return defer(() =>
                pricingDeleteApi(
                    getHttpHeaders(deps.getState()),
                    action.payload.pricingId,
                    auditData,
                    action.payload.pricingType
                )
            ).pipe(
                switchMap(() => []),
                catchError(error => of(createActionLoadingError(error)))
            )
        })
    )

export const pricingSubmittedRouterEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PRICING_FORM_SUBMITTED, CLOSE_PRICING_FORM_CLICKED, DELETE_PRICING_CLICKED),
        switchMap(() => {
            if (isMibSettingsAgendaOpened(deps.getState())) {
                return EMPTY
            }

            Router.push("/pricing")
            return EMPTY
        })
    )

export const pricingsMovedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    _deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PRICING_MOVED),
        switchMap((action: Action) => {
            const pricingId = action.payload.pricingId
            const newSortIndex = action.payload.newSortIndex
            return [
                pricingSortRequested({
                    id: pricingId,
                    sortIndex: newSortIndex,
                }),
            ]
        })
    )

export const pricingSortRequestedEpics = (
    actions$: Observable<Action>,
    _state$: StateObservable<StateObject>,
    deps: EpicDependencies
) =>
    actions$.pipe(
        ofType(PRICING_SORT_REQUESTED),
        switchMap((action: Action) => {
            return defer(() => pricingSort(getHttpHeaders(deps.getState()), action.payload.data)).pipe(
                switchMap(() => {
                    return []
                }),
                catchError(error => of(createActionLoadingError(error)))
            )
        })
    )

export default [
    pricingSortRequestedEpics,
    pricingFormSubmittedEpicsNew,
    pricingSaveRequestedEpics,
    pricingLocationSaveRequestedEpics,
    fetchSpecialPricingsDataRequestedEpics,
    specialPricingsLoadingStartedEpics,
    pricingsMovedEpics,
    pricingSubmittedRouterEpics,
    deletePricingClickedEpics,
]
